import type { QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { WatchlistSync } from '@/components/domain/watchlist-sync';
import { EnvBanner } from '@/components/layout/env-banner';
import { ErrorScreen, NotFoundScreen } from '@/components/layout/error-screen';
import { SiteFooter } from '@/components/layout/site-footer';
import { SiteHeader } from '@/components/layout/site-header';
import { WorkspaceShell } from '@/components/layout/workspace-shell';
import { useSession } from '@/hooks/use-session';

export interface RouterContext {
	queryClient: QueryClient;
}

declare module '@tanstack/react-router' {
	interface StaticDataRouteOption {
		/**
		 * Екран займає весь видимий простір і маркетингового підвалу під собою
		 * не має. Прапорець оголошує сам маршрут, а не корінь за списком
		 * шляхів: список мовчки застаріває, щойно маршрут перейменують.
		 */
		fullScreen?: boolean;
		/**
		 * Сторінка робочої зони дилера: замість маркетингової шапки й підвала
		 * її малює власний каркас із бічною панеллю (макет Paper
		 * «Dashboard — A3»). Вітрина — каталог, картка лота, маркетинг — лишається
		 * на шапці, бо туди приходять і гості.
		 */
		workspace?: boolean;
	}
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: RootLayout,
	notFoundComponent: NotFoundScreen,
	// Помилка в будь-якому маршруті ловиться тут і показує екран замість білої сторінки.
	errorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
});

function RootLayout(): ReactNode {
	/*
	  Підвал на 298px перетворював екрани входу на сторінку зі скролом навіть
	  там, де контент уміщався: 16" MacBook показував смугу прокрутки на
	  порожньому місці. У макеті цих екранів підвалу немає — артборд це рівно
	  шапка плюс контент.
	*/
	const fullScreen = useRouterState({
		select: (state) => state.matches.some((match) => match.staticData.fullScreen),
	});
	const workspace = useRouterState({
		select: (state) => state.matches.some((match) => match.staticData.workspace),
	});
	/*
	  Каркас робочої зони залежить не лише від маршруту, а й від сесії.
	  `/watchlist` відкритий гостю навмисно, а в смузі робочої зони немає
	  ні кнопки Log in, ні пунктів для гостя — він опинявся в чужому кабінеті
	  без жодного виходу з нього. Та сама умова прибирає панель одразу після
	  Log out, не чекаючи навігації.

	  Блимання немає: приватні маршрути чекають на сесію у `beforeLoad`,
	  а `/watchlist` прогріває її тим самим запитом без перевірки.
	*/
	const { isAuthenticated } = useSession();

	return (
		<div className="flex min-h-dvh flex-col">
			<EnvBanner />
			<WatchlistSync />
			{workspace && isAuthenticated ? (
				<WorkspaceShell>
					<Outlet />
				</WorkspaceShell>
			) : (
				<>
					<SiteHeader />
					<main className="flex-1">
						<Outlet />
					</main>
					{!fullScreen && <SiteFooter />}
				</>
			)}
		</div>
	);
}
